/**
 * Sandbox Page
 * Admin-only page for AI training via conversations and exemplar management.
 * Two tabs: Chat (SandboxChat) and Library (ExemplarLibrary).
 */

import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { FlaskConical } from 'lucide-react'
import { useCurrentUser } from '@/hooks'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SandboxChat } from '@/components/sandbox/SandboxChat'
import { ExemplarLibrary } from '@/components/sandbox/ExemplarLibrary'
import { Button } from '@/components/ui/button'

export function SandboxPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, isLoading } = useCurrentUser()

  // Pick up conversationId from navigation state (from ConversationHistorySheet)
  const resumeConversationId =
    (location.state as { conversationId?: string })?.conversationId || null

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-pink-500 rounded-xl flex items-center justify-center animate-pulse">
            <FlaskConical className="w-5 h-5 text-white" />
          </div>
          <div className="text-neutral-400 text-sm">Loading...</div>
        </div>
      </div>
    )
  }

  // Admin guard
  if (!user?.isAdmin) {
    return <Navigate to="/feed" replace />
  }

  return (
    <div className="min-h-screen bg-sovereign-mesh flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-violet-500/20">
              <FlaskConical className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-base tracking-tight">
                AI Sandbox
              </h1>
              <p className="text-[10px] text-neutral-500">
                Train and refine AI behavior
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/feed')}
            className="text-neutral-400 hover:text-white text-xs"
          >
            Back to Feed
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-4">
        <Tabs defaultValue="chat" className="flex flex-col h-[calc(100vh-88px)]">
          <TabsList className="grid w-full grid-cols-2 bg-neutral-900/80 border border-white/[0.06] flex-shrink-0">
            <TabsTrigger
              value="chat"
              className="data-[state=active]:bg-neutral-800 data-[state=active]:text-white text-neutral-400"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Sandbox Chat
            </TabsTrigger>
            <TabsTrigger
              value="library"
              className="data-[state=active]:bg-neutral-800 data-[state=active]:text-white text-neutral-400"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Exemplar Library
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="chat"
            className="flex-1 relative border border-white/[0.04] rounded-lg bg-neutral-950/50 overflow-hidden mt-3"
          >
            <SandboxChat resumeConversationId={resumeConversationId} />
          </TabsContent>

          <TabsContent
            value="library"
            className="flex-1 overflow-y-auto mt-3"
          >
            <ExemplarLibrary />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
